import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const SUPPORTED_ENTITY_TYPES = new Set(['contact', 'note', 'tag', 'field', 'custom_field', 'group']);
const SUPPORTED_ACTIONS = new Set(['create', 'update', 'delete']);

const isUndoableLog = (log) => (
  !log?.reverted_at &&
  (Boolean(log?.previous_state) || (log?.action === 'create' && Boolean(log?.changes))) &&
  SUPPORTED_ACTIONS.has(log?.action) &&
  SUPPORTED_ENTITY_TYPES.has(log?.entity_type)
);

export default function useUndo(enabled = true) {
  const [lastUndo, setLastUndo] = useState(null);
  const [loadingUndo, setLoadingUndo] = useState(false);
  const [loadingRedo, setLoadingRedo] = useState(false);

  // Redo stack: stores { entityType, entityId, state } snapshots taken just before each undo
  const redoStack = useRef([]);
  const [hasRedo, setHasRedo] = useState(false);

  const fetchLatestUndo = useCallback(async () => {
    if (!enabled) {
      setLastUndo(null);
      return null;
    }
    try {
      const response = await axios.get(`${API}/activity-log?limit=2000`);
      const logs = Array.isArray(response.data) ? response.data : [];
      const nextUndo = logs.find(isUndoableLog) || null;
      setLastUndo(nextUndo);
      return nextUndo;
    } catch {
      setLastUndo(null);
      return null;
    }
  }, [enabled]);

  const refreshUndo = useCallback(async () => {
    await fetchLatestUndo();
  }, [fetchLatestUndo]);

  useEffect(() => {
    refreshUndo();
  }, [refreshUndo]);

  const performUndo = useCallback(async () => {
    if (loadingUndo) return false;
    setLoadingUndo(true);
    try {
      const currentUndo = await fetchLatestUndo();
      if (!currentUndo?.id) {
        toast.error('Keine rückgängig machbare Änderung vorhanden');
        return false;
      }

      // Snapshot current state for redo — for batches fetch all sibling entries
      let batchEntries = null;
      if (currentUndo.batch_id) {
        try {
          const allLogs = await axios.get(`${API}/activity-log?limit=2000`);
          const logs = Array.isArray(allLogs.data) ? allLogs.data : [];
          batchEntries = logs.filter(l => l.batch_id === currentUndo.batch_id && !l.reverted_at);
        } catch { /* ignore, redo won't work for batch */ }
      }

      const entriesToSnapshot = batchEntries || (
        currentUndo.action === 'update' && currentUndo.entity_type && currentUndo.entity_id && currentUndo.changes
          ? [currentUndo]
          : []
      );

      const newRedoItems = entriesToSnapshot
        .filter(e => e.action === 'update' && e.entity_type && e.entity_id && e.changes)
        .map(e => ({ entityType: e.entity_type, entityId: e.entity_id, action: e.action, state: e.changes, batchId: e.batch_id }));

      if (newRedoItems.length > 0) {
        redoStack.current = [...redoStack.current, ...newRedoItems];
        setHasRedo(true);
      }

      const response = await axios.post(`${API}/activity-log/${currentUndo.id}/revert`);
      window.dispatchEvent(new CustomEvent('app:undo-complete', { detail: { logId: currentUndo.id } }));
      const count = batchEntries ? batchEntries.length : 1;
      toast.success(count > 1 ? `${count} Änderungen rückgängig gemacht` : 'Letzte Änderung rückgängig gemacht');
      await refreshUndo();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rückgängig machen fehlgeschlagen');
      return false;
    } finally {
      setLoadingUndo(false);
    }
  }, [loadingUndo, fetchLatestUndo, refreshUndo]);

  const performRedo = useCallback(async () => {
    if (loadingRedo || redoStack.current.length === 0) return false;
    setLoadingRedo(true);
    try {
      const redoItem = redoStack.current[redoStack.current.length - 1];
      if (!redoItem) return false;

      // Collect all items belonging to the same batch (same batchId, contiguous from the top of the stack)
      const batchId = redoItem.batchId;
      let itemsToRedo;
      if (batchId) {
        itemsToRedo = redoStack.current.filter(i => i.batchId === batchId);
      } else {
        itemsToRedo = [redoItem];
      }

      const applyRedo = async (item) => {
        if (item.entityType === 'contact' && item.entityId) {
          await axios.put(`${API}/contacts/${item.entityId}`, item.state);
        } else if (item.entityType === 'note' && item.entityId) {
          await axios.put(`${API}/notes/${item.entityId}`, item.state);
        } else {
          throw new Error('Wiederholen für diesen Typ nicht unterstützt');
        }
      };

      await Promise.all(itemsToRedo.map(applyRedo));

      if (batchId) {
        redoStack.current = redoStack.current.filter(i => i.batchId !== batchId);
      } else {
        redoStack.current = redoStack.current.slice(0, -1);
      }
      setHasRedo(redoStack.current.length > 0);
      window.dispatchEvent(new CustomEvent('app:undo-complete', { detail: { redo: true } }));
      toast.success(itemsToRedo.length > 1 ? `${itemsToRedo.length} Änderungen wiederhergestellt` : 'Änderung wiederhergestellt');
      await refreshUndo();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wiederholen fehlgeschlagen');
      return false;
    } finally {
      setLoadingRedo(false);
    }
  }, [loadingRedo, refreshUndo]);

  return { lastUndo, loadingUndo, loadingRedo, hasRedo, refreshUndo, performUndo, performRedo };
}
